// ============================================================
// The build palette - materials and surfaces, carried on the map.
//
// Every edit writes the terrain (so a map carries its own palette) and
// works out WHAT to write through engine/battlefield/terrainEdits.
// ============================================================
import { useState, useCallback, useMemo } from 'react';
import { DEFAULT_MATERIAL_KEY, DEFAULT_MATERIALS } from '../engine/battlefield/voxels';
import { EARTH } from '../engine/battlefield/excavation';
import { ZONE_PRESETS, defaultSurfaces, nextSurfaceKey } from '../engine/battlefield/zones';
import {
  withMaterials,
  newMaterial,
  newSurface,
  withoutEntry,
  zonesAfterSurfaceEdit,
  zonesAfterSurfaceImport,
} from '../engine/battlefield/terrainEdits';

/**
 * The cube materials. Editing one changes everything ALREADY BUILT from
 * it too, because the cubes store a key rather than a copy - which is the
 * point: correcting stone's hardness should correct every stone wall on
 * the map, not leave a dozen of them disagreeing. Restore MERGES the
 * shipped set back, so a GM's own materials survive.
 */
export function useMaterialPalette({ terrain, setTerrain, grid }, tool) {
  // TWO PICKS, one for building and one for digging (plan map-display, F): a hole is lined with what it is dug
  // in, and digging starts in earth - sharing the build pick would have lined every new trench in stone.
  const slot = tool === 'dig' ? 'dig' : 'build';
  const [picked, setPicked] = useState({ build: DEFAULT_MATERIAL_KEY, dig: EARTH.key });
  const material = picked[slot];
  const setMaterial = useCallback((key) => setPicked((prev) => ({ ...prev, [slot]: key })), [slot]);
  const materials = useMemo(() => grid.materials || DEFAULT_MATERIALS, [grid]);
  const write = useCallback((next) => setTerrain(withMaterials(terrain, grid, next)), [terrain, setTerrain, grid]);
  const editMaterial = useCallback((next) => write({ ...materials, [next.key]: next }), [write, materials]);
  const restoreMaterials = useCallback(() => write({ ...materials, ...DEFAULT_MATERIALS }), [write, materials]);
  const addMaterial = useCallback(() => {
    const next = newMaterial(materials);
    write({ ...materials, [next.key]: next });
    setMaterial(next.key);
  }, [write, materials, setMaterial]);
  const deleteMaterial = useCallback(
    (key) => {
      const left = withoutEntry(materials, key);
      if (!left) return;
      write(left.rest);
      // Both picks: the one not on screen may hold the deleted key too, and would build from a material gone.
      setPicked((prev) => ({
        build: prev.build === key ? left.fallback : prev.build,
        dig: prev.dig === key ? left.fallback : prev.dig,
      }));
    },
    [write, materials],
  );
  return { material, setMaterial, materials, editMaterial, addMaterial, deleteMaterial, restoreMaterials };
}

/**
 * The ground surfaces the Zone tool paints from, seeded from the old
 * presets (a starting set rather than the only set). An edit follows the
 * paint already on the map; restore MERGES rather than replaces, so a
 * GM's own surfaces survive and only the ones they overwrote return -
 * replacing would punish the mistake that button exists to undo.
 */
export function useSurfacePalette({ terrain, setTerrain }) {
  const [zonePreset, setZonePreset] = useState(ZONE_PRESETS[0].key);
  const surfaces = useMemo(() => terrain.surfaces || defaultSurfaces(), [terrain.surfaces]);
  const updateSurface = useCallback(
    (next) => {
      const zones = zonesAfterSurfaceEdit(terrain.zones || [], surfaces, next);
      setTerrain({ ...terrain, surfaces: { ...surfaces, [next.key]: next }, zones });
    },
    [terrain, setTerrain, surfaces],
  );
  const addSurface = useCallback(() => {
    const next = newSurface(surfaces, nextSurfaceKey());
    setTerrain({ ...terrain, surfaces: { ...surfaces, [next.key]: next } });
    setZonePreset(next.key);
  }, [terrain, setTerrain, surfaces]);
  const restoreSurfaces = useCallback(() => {
    setTerrain({ ...terrain, surfaces: { ...surfaces, ...defaultSurfaces() } });
  }, [terrain, setTerrain, surfaces]);
  const deleteSurface = useCallback(
    (key) => {
      const left = withoutEntry(surfaces, key);
      if (!left) return;
      setTerrain({ ...terrain, surfaces: left.rest });
      setZonePreset(left.fallback);
    },
    [terrain, setTerrain, surfaces],
  );
  return { zonePreset, setZonePreset, surfaces, updateSurface, addSurface, deleteSurface, restoreSurfaces };
}

/**
 * Merge an imported asset file in - incoming wins on key collision (the
 * map-library rule: refusing would make updating a shared palette
 * impossible). Colliding SURFACES restyle the ground already painted with
 * them, exactly as updateSurface would - a plain spread updates the
 * palette and never the map. One setTerrain, so undo sees one edit.
 */
export function useAssetImport({ terrain, setTerrain, grid, surfaces, materials }) {
  return useCallback(
    (incomingSurfaces, incomingMaterials) => {
      const zones = zonesAfterSurfaceImport(terrain.zones || [], surfaces, incomingSurfaces);
      const withMaterials = Object.keys(incomingMaterials).length
        ? { voxels: { ...grid, materials: { ...materials, ...incomingMaterials } } }
        : {};
      setTerrain({ ...terrain, surfaces: { ...surfaces, ...incomingSurfaces }, zones, ...withMaterials });
    },
    [terrain, setTerrain, surfaces, grid, materials],
  );
}
